import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles } from 'lucide-react';
import { API } from '../App';

/**
 * Live counter for the Founding Vendor program.
 * Pulls from GET /api/vendors/founding-program/stats every 60 seconds.
 * Auto-hides when program is full or when API is unreachable.
 */
export default function FoundingSpotsCounter() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/vendors/founding-program/stats`);
        if (alive) setStats(r.data);
      } catch (err) {
        if (alive) setStats(null);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!stats) return null;

  const limit = Number(stats.limit || 100);
  const claimed = Number(stats.claimed || 0);
  const remaining = Math.max(Number(stats.remaining ?? (limit - claimed)), 0);
  const pctClaimed = Math.min(100, Math.round((claimed / limit) * 100));

  if (remaining === 0) {
    return (
      <div
        className="inline-flex items-center gap-2 bg-white/10 border border-white/25 px-4 py-2 rounded-full backdrop-blur-sm"
        data-testid="founding-spots-full"
      >
        <span className="text-sm font-semibold text-white/90">Founding Vendor spots are FULL — join the waitlist</span>
      </div>
    );
  }

  const urgent = remaining <= 25;

  return (
    <div className="space-y-3" data-testid="founding-spots-counter">
      <div
        className={`inline-flex items-center gap-3 border rounded-full backdrop-blur-sm px-5 py-2.5 shadow-lg ${
          urgent
            ? 'bg-[hsl(var(--gold))]/25 border-[hsl(var(--gold))] animate-pulse'
            : 'bg-white/10 border-white/30'
        }`}
      >
        <Sparkles className="h-4 w-4 text-[hsl(var(--gold))]" />
        <span className="text-sm sm:text-base font-bold text-white" data-testid="founding-spots-remaining">
          Only <span className="text-[hsl(var(--gold))] text-lg">{remaining}</span> of {limit} Founding Vendor spots left
        </span>
      </div>

      <div className="max-w-md">
        <div className="h-2 rounded-full bg-white/15 overflow-hidden">
          <div
            className="h-full bg-[hsl(var(--gold))] transition-all duration-700 rounded-full"
            style={{ width: `${pctClaimed}%` }}
            data-testid="founding-spots-progress"
          />
        </div>
        <div className="flex items-center justify-between mt-1.5 text-xs text-white/70">
          <span>{claimed} claimed</span>
          <span>{pctClaimed}% full</span>
        </div>
      </div>
    </div>
  );
}
